import { z } from "zod";
import { EMPLOYEE_BASE_URL, HR_BASE_URL } from "./appConst";
import { axiosAuth } from "./common";
import { EditEmployeeFormValues } from "src/schemas/edit_employee";
import { GrantAccessSchema } from "src/schemas/grant-access-employee";
import { PayrollFormData } from "src/schemas/add-payroll-form";
import { NewHireFormValues } from "src/schemas/add-new-hire";
import { RegisterEmployeeSchema } from "src/schemas/add-employee";
import { transformForApi } from "src/utils";
import { EditPayrollSchema } from "src/schemas/edit-payroll";
import { VideoFormValues } from "src/screens/Dashboard/HR/UploadPhotos/Upload-videoes";
import axios from "axios";
import { FeedbackValues } from "src/screens/Dashboard/HR/UploadPhotos/Upload-feedback";
import { PayrollFilters } from "src/screens/Dashboard/HR/payroll/hooks/usePayroll";
import { Filters } from "src/screens/Dashboard/HR/EmployeeList/hook/useFetchArchives";

export const registerEmployee = async (
  values: z.infer<typeof RegisterEmployeeSchema>
) => {
  const api = axiosAuth();

  const standdis = transformForApi(values);
  const states = standdis.states;
  const districts = standdis.districts;

  const response = await api.post(`${HR_BASE_URL}/add-employee`, {
    full_name: values.full_name,
    date_of_birth: values.date_of_birth,
    marital_status: values.marital_status,
    gender: values.gender,
    nationality: "Indian",
    phone_number: values.phone_number,
    email: values.email,
    permanent_address: values.permanent_address,
    current_address: values.current_address,
    pin: values.pin,

    department: values.department,
    designation: values.designation,
    date_of_joining: values.date_of_joining,
    reporting_manager: values.reporting_manager || null,
    role: values.role,
    employeement_type: values.employeement_type,
    state: states,
    district: districts,
    duration: values.duration || null,
    start_date: values.start_date || null,
    end_date: values.end_date || null,

    adharcard_number: values.adharcard_number,
    pan_card_number: values.pan_card_number,
    passport_number: values.passport_number,

    bank_name: values.bank_name,
    branch: values.branch,
    bank_ac_number: values.bank_ac_number,
    ifsc_number: values.ifsc_number,

    pf_number: values.pf_number,
    uan_number: values.uan_number,
    esic_uan: values.esic_uan,

    emergency_contact_name: values.emergency_contact_name,
    emergency_contact_relationship: values.emergency_contact_relationship,
    emergency_contact_number: values.emergency_contact_number,
    emergency_contact_address: values.emergency_contact_address,
  });

  return response.data;
};

export const editEmployee = async (
  values: Partial<EditEmployeeFormValues>, id: number
) => {
  const api = axiosAuth(HR_BASE_URL);

  const payload = {
    full_name: values.full_name,
    date_of_birth: values.date_of_birth,
    marital_status: values.marital_status,
    gender: values.gender,
    nationality: "Indian",
    phone_number: values.phone_number,
    email: values.email,
    permanent_address: values.permanent_address,
    current_address: values.current_address,
    pin: values.pin,

    department: values.department,
    designation: values.designation,
    date_of_joining: values.date_of_joining,
    reporting_manager: values.reporting_manager || null,
    employeement_type: values.employeement_type,
    role: values.role,
    duration: values.duration,
    start_date: values.start_date,
    end_date: values.end_date,

    adharcard_number: values.adharcard_number,
    pan_card_number: values.pan_card_number || "",
    passport_number: values.passport_number || "",

    bank_name: values.bank_name,
    branch: values.branch,
    bank_ac_number: values.bank_ac_number || "",
    ifsc_number: values.ifsc_number || "",

    pf_number: values.pf_number || "",
    uan_number: values.uan_number || "",
    esic_uan: values.esic_uan || "",

    emergency_contact_name: values.emergency_contact_name,
    emergency_contact_relationship: values.emergency_contact_relationship,
    emergency_contact_number: values.emergency_contact_number,
    emergency_contact_address: values.emergency_contact_address,
  };

  const { data } = await api.patch(`/edit-employee/${id}`, payload);

  return data.data;
};

export const uploadEmployeeDocuments = async (
  file: File,
  id: number,
  documentType: string
) => {
  const api = axiosAuth();

  const fileName = file.name;

  const response = await api.post(
    `${HR_BASE_URL}/upload-documents?file=${fileName}`,
    {
      employee_id: id,
      document_type: documentType,
    }
  );

  return response.data;
};

export const getDocuments = async (id: number) => {
  const api = axiosAuth();

  const response = await api.get(`${HR_BASE_URL}/documents/${id}`);

  return response.data;
};

export const getEmployees = async (filters: Filters) => {
  
  const { name, employee_code, page = 1, pagesize } = filters;

  const params = new URLSearchParams();

  params.set("page", page.toString());
  params.set("pagesize", pagesize.toString());

  if (name?.trim()) params.set("fullname", name.trim());
  if (employee_code?.trim()) params.set("employee_code", employee_code.trim());
  
  const api = axiosAuth(HR_BASE_URL);

  const { data } = await api.get(`/employees?${params.toString()}`);

  return data.data.employees;
};

export const getEmployeeByCode = async (employeeCode: string) => {
  
  const api = axiosAuth(HR_BASE_URL);

  const { data } = await api.get(`/employees`, {
    params: {
      employee_code: employeeCode,
    }
  });

  return data.data.employees;
};

export const verifyEmployee = async (id: number) => {
  const api = axiosAuth();

  const response = await api.patch(`${HR_BASE_URL}/verify-employee/${id}`);

  return response.data;
};

export const grantAccess = async (
  id: number,
  values: z.infer<typeof GrantAccessSchema>
) => {
  const api = axiosAuth();

  const response = await api.post(`${HR_BASE_URL}/grant-access`, {
    id: id,
    role: values.role,
    password: values.password,
  });

  return response.data;
};

export const sendNotification = async (
  ids: number[],
  message: string,
  header: string
) => {
  const api = axiosAuth();

  const response = await api.post(`${HR_BASE_URL}/send-notifications`, {
    ids: ids,
    message: message,
    header: header,
  });

  return response.data;
};

export const addPayroll = async (values: PayrollFormData) => {
  const api = axiosAuth();

  const response = await api.post(`${HR_BASE_URL}/payroll`, {
    year: values.year,
    month: values.month,
    name: values.name,
    designation: values.designation,
    department: values.department,
    employee_id: values.employee_id,
    employee_code: values.employee_code,
    gross_salary: values.grossSalary,
    basic: values.basic,
    hra: values.hra,
    special_allowance: values.special_allowance,
    statutory_bonus: values.statutory_bonus,
    misc_allowance: values.misc_allowance,
    incentives: values.incentives,
    pf: values.pf,
    esic: values.esic,
    esic_uan: values.esic_uan,
    ap_tax: values.ap_tax,
    health_deduction: values.health_deduction,
    other_deduction: values.other_deductions,
    net_salary: values.net_salary,
    bank_name: values.bank_name,
    ac_number: values.ac_number,
    ifsc_code: values.ifsc_code,
    branch: values.branch,
    pan: values.pan,
    date_of_joining: values.date_of_joining,
    pf_account_number: values.pf_acc_number,
    uan_number: values.uan_number,
    days_worked: values.days_worked,
    lop: values.lop,
    lop_deduction: values.lop_deduction,
    pay_date: values.pay_date,
    travel_allowances: values.travel_allowances,
    otherEarnings: values.otherEarnings,
    incomeTaxDeduction: values.incomeTaxDeduction,
    reimbursementTravel: values.reimbursementTravel,
    reimbursementOthers: values.reimbursementOthers,
    totalNetPayableWords: values.totalNetPayableWords,
  });

  return response.data;
};

export const editPayroll = async (values: z.infer<typeof EditPayrollSchema>, id: string) => {
  const api = axiosAuth();

  const response = await api.patch(`${HR_BASE_URL}/payroll/${id}`, {
    year: values.year,
    month: values.month,
    name: values.name,
    designation: values.designation,
    department: values.department,
    employee_id: values.employee_id,
    employee_code: values.employee_code,
    gross_salary: values.grossSalary,
    basic: values.basic,
    hra: values.hra,
    special_allowance: values.special_allowance,
    statutory_bonus: values.statutory_bonus,
    misc_allowance: values.misc_allowance,
    incentives: values.incentives,
    pf: values.pf,
    esic: values.esic,
    esic_uan: values.esic_uan,
    ap_tax: values.ap_tax,
    health_deduction: values.health_deduction,
    other_deduction: values.other_deductions,
    net_salary: values.net_salary,
    bank_name: values.bank_name,
    ac_number: values.ac_number,
    ifsc_code: values.ifsc_code,
    branch: values.branch,
    pan: values.pan,
    date_of_joining: values.date_of_joining,
    pf_account_number: values.pf_acc_number,
    uan_number: values.uan_number,
    days_worked: values.days_worked,
    lop: values.lop,
    lop_deduction: values.lop_deduction,
    pay_date: values.pay_date,
    travel_allowances: values.travel_allowances,
    otherEarnings: values.otherEarnings,
    incomeTaxDeduction: values.incomeTaxDeduction,
    reimbursementTravel: values.reimbursementTravel,
    reimbursementOthers: values.reimbursementOthers,
    totalNetPayableWords: values.totalNetPayableWords,
  });

  return response.data;
};

export const getPayroll = async (filters: PayrollFilters) => {

  const { month, year, employee_code, page = 1 } = filters;

  const params = new URLSearchParams();

  params.set("page", page.toString());

  if (month?.trim()) params.set("month", month.trim());
  if (year?.trim()) params.set("year", year.trim());
  if (employee_code?.trim()) params.set("employee_code", employee_code.trim());

  const api = axiosAuth(HR_BASE_URL);

  const { data } = await api.get(`/payroll?pagesize=25&${params.toString()}`);

  return data.data.payroll;
};

export const addNewHire = async (values: NewHireFormValues) => {
  const api = axiosAuth();

  const response = await api.post(`${HR_BASE_URL}/new-hire`, {
    full_name: values.full_name,
    email: values.email,
    address: values.address,
    position: values.position,
    contact_number: values.contact_number,
    employment_type: values.employment_type,
    letter_type: values.letter_type,
  });

  return response.data;
};

export const getNewHires = async (page) => {
  const api = axiosAuth();

  const response = await api.get(`${HR_BASE_URL}/new-hire?page=${page}&pagesize=15`);

  return response.data;
};

export const updateNewHireStatus = async (
  id: string,
  values,
) => {
  const api = axiosAuth();

  const response = await api.patch(`${HR_BASE_URL}/new-hire/${id}`, {
    status: values.status,
    employment_type: values.employment_type,
  });

  return response.data;
};

export const getSummary = async () => {

  const api = axiosAuth(HR_BASE_URL);

  const { data } = await api.get(`/total`);

  return data.data.total;
}

export const getHolidays = async () => {
  const api = axiosAuth();

  const res = await api.get(`${HR_BASE_URL}/holiday`);

  return res.data;
}

export const archiveEmployee = async (id: number) => {
  const api = axiosAuth();

  const res = await api.patch(`${HR_BASE_URL}/employee/archive/${id}`);

  return res.data;
}

export const getArchivedEmployees = async (filter: Filters) => {

  const { name, employee_code, page = 1, pagesize } = filter;

  const params = new URLSearchParams();

  params.set("page", page.toString());
  params.set("pagesize", pagesize.toString());

  if (name?.trim()) params.set("fullname", name.trim());
  if (employee_code?.trim()) params.set("employee_code", employee_code.trim());

  const api = axiosAuth(HR_BASE_URL);

  const { data } = await api.get(`/employees/archived?${params.toString()}`);

  return data.data.archived_employees;
};

export const unarchiveEmployee = async (id: number) => {
  const api = axiosAuth();

  const res = await api.patch(`${HR_BASE_URL}/employee/unarchive/${id}`);

  return res.data;
}

export const deleteEmployee = async (employeeId: number) => {
  const api = axiosAuth(HR_BASE_URL);

  const { data } = await api.delete(`/employee/${employeeId}`);
  return data;
};

// delete assigned district from employee
export const deleteDistrict = async (id: string) => {
  const api = axiosAuth();

  const res = await api.delete(`${HR_BASE_URL}/employee/district/${id}`);

  return res.data;
}

// delete assigned state from employee
export const deleteState = async (id: string) => {
  const api = axiosAuth();

  const res = await api.delete(`${HR_BASE_URL}/employee/state/${id}`);

  return res.data;
}

// assign district to employee
export const addDistrict = async (employee_id: number, district: string) => {
  const api = axiosAuth();

  const res = await api.post(`${HR_BASE_URL}/employee/district`, {
    employee_id: employee_id,
    assigned_district: district,
  });

  return res.data;
}

// assign state to employee
export const addState = async (employee_id: number, state: string) => {
  const api = axiosAuth();

  const res = await api.post(`${HR_BASE_URL}/employee/state`, {
    employee_id: employee_id,
    assigned_state: state,
  });

  return res.data;
}


export const uploadPhotos = async (file: File) => {
  if (!file) return;
  
    const api = axiosAuth();

    const res = await api.get(
      `${HR_BASE_URL}/upload-pic/hr?file_name=${file.name}`
    );

    const url = res.data.data.data.url;
    console.log("url", url);

    await axios.put(url, file);

    return res.data.data.data.Key;
};

export const submitUploadedPic = async (
  state: string,
  key: string
) => {
  
    const api = axiosAuth();
    await api.post(`${HR_BASE_URL}/save-upload-pic/hr`, {
      state,
      key
    });
};

export const uploadVideo = async (file: File) => {
  if (!file) return;

  
    const api = axiosAuth();
    const res = await api.get(`${HR_BASE_URL}/latest-update/upload?file_name=${file.name}&file_type=${file.type}`);
    const url = res.data.data.data.url;

    axios.put(url, file);

    return res.data.data.data.Key;
}

export const submitUploadVideo = async (values: VideoFormValues) => {

    const api = axiosAuth();

    // Send backend fields exactly
     await api.post(`${HR_BASE_URL}/latest-update`, {
      thumbnail_key: values.thumbnail_key,
      video_key: values.video_key,
      name: values.name,
      post: values.post,
      heading: values.heading,
      subheading: values.subheading,
    });
};

export const uploadFeedback = async (file: File) => {
  if (!file) return;
  
    const api = axiosAuth();
    const res = await api.get(`${HR_BASE_URL}/feedback/upload?file_name=${file.name}&file_type=${file.type}`);
    const url = res.data.data.data.url;

    axios.put(url, file);

    return res.data.data.data.Key;
}

export const submitUploadFeedback = async (values: FeedbackValues) => {
  
    const api =axiosAuth();
    await api.post(`${HR_BASE_URL}/feedback`, {
      thumbnail_key: values.thumbnail_key,
      location: values.location,
      url: values.url,
    })
};


export const getPhotos = async () => {
  try {
    const res = await axios.get(`${HR_BASE_URL}/uploaded-pic/state`);
    return res.data.data.pics; 
    
  } catch (error) {
    console.log("error", error);
  }
};

export const getVideoes = async (Page: number, pageSize: number) => {
    try {
        const res = await axios.get(`${HR_BASE_URL}/latest-update?page=${Page}&page_size=${pageSize}`);
        return res.data.data.latestUpdates;

    }
    catch (error) {
        console.error("Error", error);
    }
}

export const getFeedbackvideos = async(Page: number, PageSize: number) => {
    try {
        const res= await axios.get(`${HR_BASE_URL}/feedback?page=${Page}&page_size=${PageSize}`);
        return res.data.data.feedbackVideos;
    } catch (error) {
        console.error(error);
    }
}

export const deletePhoto = async (id: string) => {
  try {
      const api = axiosAuth();
      await api.delete(`${HR_BASE_URL}/uploaded-pic/${id}`);
  } catch (error) {
    console.error("error", error);
  }
}

export const deleteVideo = async (id: string) => {
  try {
    const api = axiosAuth();
    await api.delete(`${HR_BASE_URL}/latest-update/${id}`);
  } catch (error) {
    console.error("error", error);
  }
}

export const deleteFeedback = async(id: string) => {
  try {
    const api = axiosAuth();
    await api.delete(`${HR_BASE_URL}/feedback/${id}`);
  } catch (error) {
    console.error(error);
  }
}

export const getDownloadUrl = async(key: string) => {
  try {
    const api = axiosAuth();
    const response = await api.get(`${EMPLOYEE_BASE_URL}/media/url?key=${encodeURIComponent(key)}`);

    return response?.data.data.url;

  } catch (error) {
    console.error(error, "Failed to get download url");
  }
}